import { useMemo } from 'react';

// Embed mode: the signup form rendered inside an iframe on calibrite.com.
//
// Presentation is owned by the host page, so embed mode drops everything that
// only makes sense on our own standalone page — the modal overlay, the body
// scroll-lock, the focus trap, viewport centring, the outer padding and border,
// the document background, and the language switcher (the host site's locale
// system already decides language, and passes it as ?lang=).
//
// Callable outside React because public/index.html runs the same check inline
// to set the .cb-embed class before first paint.

export const isEmbedMode = () => {
  try {
    return new URLSearchParams(window.location.search).get('embed') === '1';
  } catch {
    return false;
  }
};

// Card width in embed mode. The host page owns the frame, and we cannot measure
// it across origins, so 640 is a default rather than a fit: wide enough not to
// look cramped in a content column, bounded so a full-width container cannot
// stretch the country select to 1200px.
//
// ?maxw= overrides it — an integer of px, or `full` for no cap. Only read in
// embed mode; the standalone page is unaffected.
export const EMBED_DEFAULT_MAX_WIDTH = 640;
const EMBED_MIN_MAX_WIDTH = 280; // below this the two-column field rows break
const EMBED_MAX_MAX_WIDTH = 1200;

export const getEmbedMaxWidth = () => {
  const fallback = EMBED_DEFAULT_MAX_WIDTH + 'px';
  try {
    const raw = (new URLSearchParams(window.location.search).get('maxw') || '').trim();
    if (!raw) return fallback;
    if (raw === 'full') return 'none';

    const digits = raw.replace(/px$/i, '');
    if (!/^\d+$/.test(digits)) {
      console.warn(`Ignoring maxw="${raw}" — expected an integer of px, or "full".`);
      return fallback;
    }

    const n = Number(digits);
    if (n < EMBED_MIN_MAX_WIDTH || n > EMBED_MAX_MAX_WIDTH) {
      console.warn(
        `Ignoring maxw=${n} — outside ${EMBED_MIN_MAX_WIDTH}-${EMBED_MAX_MAX_WIDTH}px.`
      );
      return fallback;
    }
    return n + 'px';
  } catch {
    return fallback;
  }
};

function useEmbedMode() {
  return useMemo(() => isEmbedMode(), []);
}

export function useEmbedMaxWidth() {
  return useMemo(() => getEmbedMaxWidth(), []);
}

export default useEmbedMode;
